import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Campo, Etiqueta, useAcao, useCarga } from '../comum';

/**
 * Gestão de painéis.
 *
 * 🔴 **Isto já existia inteiro no servidor e não tinha tela.** Criar vários
 * painéis, renomear, compartilhar com o time e escolher qual abre primeiro —
 * tudo pronto, tudo invisível. "Meu painel" era um título morto.
 *
 * Importa para a venda: *"o gestor tem o painel dele, o técnico tem o dele, e o
 * da diretoria é compartilhado"* é uma frase que fecha reunião, e o produto já
 * fazia isso sem conseguir mostrar.
 */

interface Painel {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string;
  readonly dono: string;
  readonly compartilhado: boolean;
  readonly inicial: boolean;
  readonly cartoes: readonly unknown[];
}

export function GerenciarPaineis({
  aoPerderSessao,
  meuLogin,
  aoFechar,
  aoMudar,
}: {
  aoPerderSessao: () => void;
  meuLogin: string;
  aoFechar: () => void;
  /** Avisa a tela inicial para recarregar — o painel exibido pode ter mudado. */
  aoMudar: () => void;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [renomeando, setRenomeando] = useState<string | null>(null);
  const [nome, setNome] = useState('');

  const lista = useCarga<{ itens: readonly Painel[] }>(
    () => api.get('/api/paineis'),
    [],
    aoPerderSessao,
  );

  const recarregar = (): void => {
    lista.recarregar();
    aoMudar();
  };

  const criar = (): void =>
    void acao.rodar(async () => {
      await api.post('/api/paineis', {
        nome: 'Painel novo',
        descricao: '',
        cartoes: [],
      });
      recarregar();
      return 'Painel criado. Adicione cartões perguntando ao inventário.';
    });

  const salvarNome = (p: Painel): void =>
    void acao.rodar(async () => {
      await api.put(`/api/paineis/${p.id}`, { nome: nome.trim() });
      setRenomeando(null);
      recarregar();
      return 'Nome alterado.';
    });

  const tornarInicial = (p: Painel): void =>
    void acao.rodar(async () => {
      await api.post(`/api/paineis/${p.id}/inicial`);
      recarregar();
      return `"${p.nome}" agora é o painel que abre na tela inicial.`;
    });

  const alternarCompartilhado = (p: Painel): void =>
    void acao.rodar(async () => {
      await api.put(`/api/paineis/${p.id}`, { compartilhado: !p.compartilhado });
      recarregar();
      return p.compartilhado
        ? 'Painel deixou de ser compartilhado.'
        : 'Painel compartilhado: o time inteiro passa a vê-lo.';
    });

  const excluir = (p: Painel): void =>
    void acao.rodar(async () => {
      await api.del(`/api/paineis/${p.id}`);
      recarregar();
      return 'Painel excluído.';
    });

  return (
    <div className="editor-cartao">
      <header>
        <strong>Meus painéis</strong>
        <button className="icone" onClick={aoFechar} aria-label="Fechar">
          ✕
        </button>
      </header>

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {lista.dados !== null && lista.dados.itens.length === 0 && (
        <p className="suave">
          Nenhum painel ainda. Crie um e monte os cartões perguntando ao inventário.
        </p>
      )}

      <div className="rolagem">
        <table className="densa">
          <tbody>
            {(lista.dados?.itens ?? []).map((p) => {
              // Painel de outra pessoa aparece porque foi compartilhado — dá para
              // ver, não para mexer. O servidor recusa de qualquer forma; a tela
              // não oferece o botão para ninguém tentar.
              const meu = p.dono === meuLogin;

              return (
                <tr key={p.id}>
                  <td className="forte">
                    {renomeando === p.id ? (
                      <Campo rotulo="">
                        <input
                          value={nome}
                          maxLength={60}
                          autoFocus
                          onChange={(e) => setNome(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') salvarNome(p);
                            if (e.key === 'Escape') setRenomeando(null);
                          }}
                        />
                      </Campo>
                    ) : (
                      p.nome
                    )}
                    <div className="suave" style={{ fontSize: 12.5 }}>
                      {p.cartoes.length} cartão(ões)
                      {!meu && ` · de ${p.dono}`}
                    </div>
                  </td>
                  <td>
                    {p.inicial && <Etiqueta tom="ok">abre primeiro</Etiqueta>}{' '}
                    {p.compartilhado && <Etiqueta tom="neutro">compartilhado</Etiqueta>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {renomeando === p.id ? (
                      <>
                        <button
                          className="primario"
                          disabled={acao.executando || nome.trim() === ''}
                          onClick={() => salvarNome(p)}
                        >
                          Salvar
                        </button>{' '}
                        <button onClick={() => setRenomeando(null)}>Cancelar</button>
                      </>
                    ) : (
                      meu && (
                        <>
                          {!p.inicial && (
                            <>
                              <button
                                disabled={acao.executando}
                                title="Este passa a ser o painel da tela inicial"
                                onClick={() => tornarInicial(p)}
                              >
                                Abrir primeiro
                              </button>{' '}
                            </>
                          )}
                          <button
                            onClick={() => {
                              setRenomeando(p.id);
                              setNome(p.nome);
                            }}
                          >
                            Renomear
                          </button>{' '}
                          <button
                            disabled={acao.executando}
                            title={
                              p.compartilhado
                                ? 'Deixa de aparecer para os outros'
                                : 'O time inteiro passa a ver este painel'
                            }
                            onClick={() => alternarCompartilhado(p)}
                          >
                            {p.compartilhado ? 'Tornar privado' : 'Compartilhar'}
                          </button>{' '}
                          {/*
                            Excluir o painel que abre primeiro deixaria a tela
                            inicial órfã. O servidor não impede; a tela sim, e
                            explica o caminho: escolha outro antes.
                          */}
                          <button
                            className="perigo"
                            disabled={acao.executando || p.inicial}
                            title={
                              p.inicial
                                ? 'Escolha outro painel para abrir primeiro antes de excluir este'
                                : 'Excluir painel'
                            }
                            onClick={() => excluir(p)}
                          >
                            Excluir
                          </button>
                        </>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="linha">
        <button className="primario encolhe" disabled={acao.executando} onClick={criar}>
          + Novo painel
        </button>
        <span className="cresce" />
      </div>
    </div>
  );
}
